import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import {
  useEmailTemplatesList,
  useDeleteEmailTemplate,
  useUpdateEmailTemplate,
  TEMPLATE_CATEGORIES,
  getTemplateCategory,
  getTemplateTypeLabel,
  type EmailTemplate,
  type EmailTemplateFilters,
} from './hooks/useEmailTemplates';
import { EmailTemplateEditor } from './EmailTemplateEditor';

/**
 * All known notification types that send emails in the system.
 * Grouped by category, using the actual type strings from the API codebase.
 */
const ALL_EMAIL_TYPES: Record<string, string[]> = {
  auth: ['auth.account_locked', 'auth.password_reset_requested', 'auth.password_changed'],
  user: ['user_invitation'],
  contact: ['lead_stale_reminder', 'unassigned_prospects_alert'],
  job: [
    'job_overdue',
    'job_due_date_reminder',
    'task_assigned_to_client',
    'recurring_job_created',
    'reminder_due',
  ],
  billing: ['invoice_overdue_reminder'],
  sla: ['sla_warning', 'sla_breach', 'sla_escalation'],
  document: [
    'document.request_created',
    'document.request_fulfilled',
    'document.request_overdue',
    'document_shared',
    'document_malware_detected',
    'portal_document_uploaded',
  ],
  portal: ['portal_invitation', 'portal_mfa_code', 'client.message'],
};

/**
 * EmailTemplatesPage — manage configurable email templates for all notification types.
 * Shows ALL known email types — configured ones display their template, unconfigured
 * ones show a "Not Configured" badge with a button to set one up.
 *
 * Requirements: 13.3 — Configurable email templates with merge fields
 */
export function EmailTemplatesPage() {
  const { toast } = useToast();

  // ─── State ──────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState<EmailTemplateFilters['channel'] | ''>('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [createForType, setCreateForType] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // ─── Data ───────────────────────────────────────────────────────────────────
  const filters: EmailTemplateFilters = {};
  if (channelFilter) filters.channel = channelFilter;

  const { data: templates, isLoading } = useEmailTemplatesList(filters);
  const deleteMutation = useDeleteEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();

  // Build a map of type => template for quick lookup
  const templateMap = useMemo(() => {
    const map = new Map<string, EmailTemplate>();
    if (templates) {
      for (const t of templates) {
        // For display purposes, use the first email-channel template per type
        if (!map.has(t.type) || t.channel === 'email') {
          map.set(t.type, t);
        }
      }
    }
    return map;
  }, [templates]);

  // Build merged list: all known types + any extra templates in DB not in our known list
  const allTypes = useMemo(() => {
    const known = new Set<string>();
    const result: Record<string, string[]> = {};

    for (const [cat, types] of Object.entries(ALL_EMAIL_TYPES)) {
      result[cat] = [...types];
      for (const t of types) known.add(t);
    }

    // Add any templates from DB that aren't in our known list
    if (templates) {
      for (const t of templates) {
        if (!known.has(t.type)) {
          const cat = getTemplateCategory(t.type);
          if (!result[cat]) result[cat] = [];
          result[cat].push(t.type);
          known.add(t.type);
        }
      }
    }

    return result;
  }, [templates]);

  // ─── Filtering ──────────────────────────────────────────────────────────────
  const filteredCategories = useMemo(() => {
    const categoryOrder = Object.keys(TEMPLATE_CATEGORIES);
    let categories = Object.keys(allTypes);

    // Filter by category
    if (categoryFilter) {
      categories = categories.filter((c) => c === categoryFilter);
    }

    // Sort by defined order
    categories.sort((a, b) => {
      const ai = categoryOrder.indexOf(a);
      const bi = categoryOrder.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return categories;
  }, [allTypes, categoryFilter]);

  const getFilteredTypes = (category: string): string[] => {
    const types = allTypes[category] || [];
    if (!search.trim()) return types;
    const term = search.toLowerCase();
    return types.filter(
      (type) =>
        type.toLowerCase().includes(term) ||
        getTemplateTypeLabel(type).toLowerCase().includes(term) ||
        (templateMap.get(type)?.subjectTemplate?.toLowerCase().includes(term) ?? false),
    );
  };

  const isSearching = search.trim().length > 0;

  // Categories that actually have visible rows (after search filtering).
  const visibleCategories = filteredCategories.filter((c) => getFilteredTypes(c).length > 0);

  // While searching, sections force-expand so matches stay visible.
  const allExpanded = visibleCategories.every((c) => isSearching || !collapsed[c]);

  const toggleExpandCollapseAll = () => {
    if (allExpanded) {
      const next: Record<string, boolean> = {};
      for (const c of visibleCategories) next[c] = true;
      setCollapsed(next);
    } else {
      setCollapsed({});
    }
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditingTemplate(null);
    setCreateForType(null);
    setIsEditorOpen(true);
  };

  const handleConfigureType = (type: string) => {
    setEditingTemplate(null);
    setCreateForType(type);
    setIsEditorOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setCreateForType(null);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingTemplate(null);
    setCreateForType(null);
  };

  const handleToggleActive = (template: EmailTemplate) => {
    updateMutation.mutate(
      { id: template.id, data: { is_active: !template.isActive } },
      {
        onSuccess: () => {
          toast({
            title: template.isActive ? 'Template disabled' : 'Template enabled',
            variant: template.isActive ? 'info' : 'success',
          });
        },
      },
    );
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setDeleteConfirmId(null);
        toast({ title: 'Template deleted', variant: 'success' });
      },
    });
  };

  // ─── Category filter options ────────────────────────────────────────────────
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...Object.entries(TEMPLATE_CATEGORIES).map(([value, label]) => ({ value, label })),
  ];

  const channelOptions = [
    { value: '', label: 'All Channels' },
    { value: 'email', label: 'Email' },
    { value: 'in_app', label: 'In-App' },
    { value: 'push', label: 'Push' },
  ];

  // ─── Loading State ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"
            role="status"
            aria-label="Loading email templates"
          />
        </div>
      </Card>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Email Templates"
          description="Configure the subject and body of notification emails sent by the system. Use merge fields like {{client.first_name}} for dynamic content."
          action={
            <Button size="sm" onClick={handleCreate}>
              Add Template
            </Button>
          }
        />
        <CardContent>
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search email templates"
              />
            </div>
            <div className="w-48">
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              />
            </div>
            <div className="w-36">
              <Select
                options={channelOptions}
                value={channelFilter}
                onChange={(e) =>
                  setChannelFilter(e.target.value as EmailTemplateFilters['channel'] | '')
                }
                aria-label="Filter by channel"
              />
            </div>
            {visibleCategories.length > 0 && (
              <button
                type="button"
                onClick={toggleExpandCollapseAll}
                className="ml-auto text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>

          {/* Template List Grouped by Category */}
          <div className="space-y-4">
            {filteredCategories.map((category) => {
              const types = getFilteredTypes(category);
              if (types.length === 0) return null;

              // Auto-expand while searching; otherwise honour the toggle.
              const isOpen = isSearching || !collapsed[category];

              return (
                <div
                  key={category}
                  className="overflow-hidden rounded-lg border border-border dark:border-border-dark"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }))
                    }
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-2 bg-surface-secondary px-4 py-2.5 text-left text-sm font-semibold text-gray-700 hover:bg-surface-secondary/70 dark:bg-surface-dark-tertiary dark:text-gray-300 dark:hover:bg-surface-dark-tertiary/70"
                  >
                    <svg
                      className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                    <span>{TEMPLATE_CATEGORIES[category] || category}</span>
                    <Badge variant="default" size="sm">
                      {types.length}
                    </Badge>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto border-t border-border dark:border-border-dark">
                      <table className="w-full text-sm" role="table">
                        <thead>
                          <tr className="border-b border-border bg-surface-secondary text-left dark:border-border-dark dark:bg-surface-dark-tertiary">
                            <th className="px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                              Type
                            </th>
                            <th className="px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                              Subject
                            </th>
                            <th className="px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                              Status
                            </th>
                            <th className="px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                              Trigger Statuses
                            </th>
                            <th className="px-4 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border dark:divide-border-dark">
                          {types.map((type) => {
                            const tmpl = templateMap.get(type);
                            return (
                              <tr
                                key={type}
                                className="hover:bg-surface-secondary/50 dark:hover:bg-surface-dark-tertiary/50"
                              >
                                <td className="px-4 py-3">
                                  <div>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {getTemplateTypeLabel(type)}
                                    </span>
                                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                      {type}
                                    </span>
                                  </div>
                                </td>
                                <td className="max-w-[250px] truncate px-4 py-3 text-gray-600 dark:text-gray-300">
                                  {tmpl?.subjectTemplate || (
                                    <span className="italic text-gray-400">
                                      Using system default
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {tmpl ? (
                                    <Badge
                                      variant={tmpl.isActive ? 'success' : 'default'}
                                      size="sm"
                                      dot
                                    >
                                      {tmpl.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                  ) : (
                                    <Badge variant="warning" size="sm">
                                      Not Configured
                                    </Badge>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {tmpl?.triggerStatuses && tmpl.triggerStatuses.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {tmpl.triggerStatuses.map((s) => (
                                        <span
                                          key={s}
                                          className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                        >
                                          {s}
                                        </span>
                                      ))}
                                    </div>
                                  ) : tmpl ? (
                                    <span className="text-xs text-gray-400">All statuses</span>
                                  ) : null}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {tmpl ? (
                                      <>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleEdit(tmpl)}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleToggleActive(tmpl)}
                                        >
                                          {tmpl.isActive ? 'Disable' : 'Enable'}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setDeleteConfirmId(tmpl.id)}
                                        >
                                          Delete
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleConfigureType(type)}
                                      >
                                        Configure
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary */}
          {templates && (
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {templates.length} template{templates.length !== 1 ? 's' : ''} configured &middot;{' '}
              {templates.filter((t) => t.isActive).length} active
            </p>
          )}
        </CardContent>
      </Card>

      {/* Template Editor */}
      {isEditorOpen && (
        <EmailTemplateEditor
          template={editingTemplate}
          defaultType={createForType}
          onClose={handleEditorClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <Modal open onClose={() => setDeleteConfirmId(null)} title="Delete Template" size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this email template? Notifications of this type will
              fall back to the system default content. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={() => handleDelete(deleteConfirmId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
